import React, { useRef, useState } from "react";
import { _post } from "../services/apiClient";
import { registerUser, resetUserData } from "../services/authService";
import { apiUrls } from "../config/apiUrls.const";
import { showToast } from "../services/alertService";
import TextInput from "./components/TextInput";
import FileInput from "./components/FileInput";
import LocationSelector from "./components/LocationSelector";
import LocationPickerMap, { LocationPickerRef } from "./components/LocationPickerMap";

type ValidationErrors = Record<string, string[]>;

export default function Register() {
    const [formData, setFormData] = useState({
        name: "",
        mobileNo: "",
        email: "",
        password: "",
        confirmPassword: "",
        agreement: false,
        isBusinessClient: false,
        type: "member",
        allowed_service: "sparlon",
    });

    const [businessData, setBusinessData] = useState({
        name: "",
        logo: null as File | null,
        biz_mobile_no: "",
        biz_email: "",
        division_id: "",
        district_id: "",
        thana_id: "",
        postal_code: "",
        address: "",
        lat: 23.8103,
        lon: 90.4125,
        map_image: null as File | null,
    });

    const [errors, setErrors] = useState<ValidationErrors>({});
    const [loading, setLoading] = useState(false);
    const [businessNameExists, setBusinessNameExists] = useState(false);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    // --- Common input change ---
    const handleInputChange =
        (field: keyof typeof formData) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const value =
                    e.target.type === "checkbox"
                        ? (e.target as HTMLInputElement).checked
                        : e.target.value;

                if (field === "isBusinessClient") {
                    setFormData((prev) => ({
                        ...prev,
                        isBusinessClient: value as boolean,
                        type: value ? "client" : "member",
                    }));
                } else {
                    setFormData((prev) => ({ ...prev, [field]: value }));
                }

                // sync contact info to business form
                if (field === "mobileNo") {
                    setBusinessData((prev) => ({ ...prev, mobile_no: value.toString() }));
                }
                if (field === "email") {
                    setBusinessData((prev) => ({ ...prev, email: value.toString() }));
                }
            };

    // --- Business input change ---
    const handleBusinessChange = (name: string, value: any) => {
        setBusinessData((prev) => ({ ...prev, [name]: value }));
    };

    // --- Logo upload ---
    const handleLogoChange = (name: string, files: FileList | null) => {
        if (files && files.length > 0) {
            const file = files[0];
            setBusinessData((prev) => ({ ...prev, logo: file }));
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const mapRef = useRef<LocationPickerRef>(null);

    // --- Check if business name exists ---
    const checkBusinessName = async (): Promise<boolean> => {
        const name = businessData.name.trim();
        if (!name) return false;
        try {
            const res = await _post(apiUrls.checkBusinessName, { name });
            const exists = !!res?.data?.data;
            setBusinessNameExists(exists);
            return exists;
        } catch {
            return false;
        }
    };

    // --- Submit ---
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        if (formData.isBusinessClient) {
            const exists = await checkBusinessName();
            if (exists) {
                showToast({
                    icon: "warning",
                    title: "Business name already exists",
                    text: "Please choose another one.",
                });
                setLoading(false);
                return;
            }
        }

        try {
            // --- Register user ---
            const userData = await registerUser(
                formData.name,
                formData.mobileNo,
                formData.email,
                formData.password,
                formData.confirmPassword,
                formData.type,
                formData.allowed_service
            );

            if (!userData.success) {
                if (userData.type === "validation") {
                    setErrors(userData.errors);
                    Object.entries(userData.errors).forEach(([_, msgs]) =>
                        (msgs as string[]).forEach((msg) =>
                            showToast({
                                icon: "error",
                                title: "Validation Error",
                                text: msg,
                            })
                        )
                    );
                } else {
                    showToast({
                        icon: "error",
                        title: "Error",
                        text: userData.message || "Registration failed.",
                    });
                }
                setLoading(false);
                return;
            }

            // --- Register business ---
            if (formData.isBusinessClient) {

                let mapImage = businessData.map_image;

                if (!mapImage && mapRef.current) {
                    mapImage = await mapRef.current.generateStaticImage();
                }

                const form = new FormData();

                Object.entries(businessData).forEach(([k, v]) => {
                    if (k !== 'map_image' && v !== null && v !== undefined) {
                        form.append(k, v as any);
                    }
                });

                if (mapImage) {
                    form.append("map_image", mapImage);
                }

                const res = await _post(apiUrls.storeBusiness, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if(res.data?.success){
                    showToast({
                        icon: "success",
                        title: "Success",
                        text: "Business registered successfully!",
                    });
            
                    const checkUserData = await resetUserData();
                    if(checkUserData?.data?.success){
                        window.location.href = "/";
                    }
                }
            }

            showToast({
                icon: "success",
                title: "Registration successful!",
                text: "Redirecting...",
            });
            window.location.reload();
        } catch (err: any) {
            showToast({
                icon: "error",
                title: "Error",
                text: err.message || "Something went wrong.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <h1 className="page-header">User Registration</h1>
        <hr className="section-devider" />

        <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* --- Left: User info --- */}
                    <div className="col-md-4">
                        <TextInput
                            label="User Name"
                            name="name"
                            value={formData.name}
                            required
                            onChange={(name, value) => handleInputChange("name")({ target: { value } } as any)}
                        />
                    </div>
                    <div className="col-md-4">
                        <TextInput
                            label="Mobile No"
                            name="mobileNo"
                            value={formData.mobileNo}
                            required
                            onChange={(name, value) => handleInputChange("mobileNo")({ target: { value } } as any)}
                        />
                    </div>
                    <div className="col-md-4">
                        <TextInput
                            label="Email"
                            name="email"
                            type="email"
                            value={formData.email}
                            required
                            onChange={(name, value) => handleInputChange("email")({ target: { value } } as any)}
                        />
                    </div>
                    <div className="col-md-4">
                        <TextInput
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            required
                            onChange={(name, value) =>
                                handleInputChange("password")({ target: { value } } as any)
                            }
                        />
                    </div>
                    <div className="col-md-4">
                        <TextInput
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            required
                            onChange={(name, value) =>
                                handleInputChange("confirmPassword")({ target: { value } } as any)
                            }
                        />
                    </div>
                    <div className="col-md-12 mb-3">
                        <div className="form-group">
                            <div className="form-check mt-2">
                            <input
                                type="checkbox"
                                id="agreement"
                                className="form-check-input"
                                checked={formData.agreement}
                                onChange={handleInputChange("agreement")}
                                required
                            />
                            <label htmlFor="agreement" className="form-check-label">
                                I agree to the terms and conditions
                            </label>
                        </div>
                        </div>
                    </div>

                    {/* --- Right: Business info --- */}
                    <div className="col-md-12">
                        
                        <div className="form-group">
                            <div className="form-check form-switch mb-3">
                                <input
                                    type="checkbox"
                                    id="isBusinessClient"
                                    className="form-check-input"
                                    checked={formData.isBusinessClient}
                                    onChange={handleInputChange("isBusinessClient")}
                                />
                                <label className="form-check-label fw-bold" htmlFor="isBusinessClient">
                                    Register as Business Client
                                </label>
                            </div>
                        </div>

                        {formData.isBusinessClient && (
                            <>
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label htmlFor="businessName" className="form-label">
                                                        Business Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        id="businessName"
                                                        name="name"
                                                        className="form-control"
                                                        value={businessData.name}
                                                        required
                                                        onChange={(e) => handleBusinessChange("name", e.target.value)}
                                                        onBlur={checkBusinessName}
                                                    />
                                                </div>
                                                {businessNameExists && (
                                                    <div className="text-danger">Business name already exists</div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <TextInput
                                                    label="Business Mobile No"
                                                    name="biz_mobile_no"
                                                    value={businessData.biz_mobile_no}
                                                    required
                                                    onChange={(name, value) => handleBusinessChange("biz_mobile_no", value)}
                                                />

                                            </div>
                                            <div className="col-md-6">
                                                <TextInput
                                                    label="Business Email"
                                                    name="biz_email"
                                                    type="email"
                                                    value={businessData.biz_email}
                                                    required
                                                    onChange={(name, value) => handleBusinessChange("biz_email", value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <FileInput
                                                    label="Business Logo"
                                                    name="logo"
                                                    multiple={false}
                                                    existing={logoPreview}
                                                    onChange={handleLogoChange}
                                                />
                                            </div>
                                        </div>
                                            <LocationSelector
                                                setClass="col-md-6"
                                                division_id={businessData.division_id}
                                                district_id={businessData.district_id}
                                                thana_id={businessData.thana_id}
                                                postal_code={businessData.postal_code}
                                                onChange={handleBusinessChange}
                                                required
                                            />
                                        <div className="mb-3">

                                            <TextInput
                                                label="Address"
                                                name="address"
                                                value={businessData.address}
                                                required
                                                onChange={handleBusinessChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label>Business Location</label>
                                            <LocationPickerMap
                                                ref={mapRef}
                                                mode="create"
                                                value={businessData.lat && businessData.lon ? { lat: businessData.lat, lng: businessData.lon } : null}
                                                onChange={(loc) =>
                                                setBusinessData((p) => ({ ...p, lat: loc?.lat, lon: loc?.lng }))
                                                }
                                            />
                                            <small className="text-muted">
                                                Lat: {businessData.lat} | Lon: {businessData.lon}
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-danger mt-2">
                                    Note: Business registration includes a <b>45-day trial</b>.
                                </p>
                            </>
                        )}
                    </div>

                    <div className="col-12 mt-3">
                        <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                            {loading ? "Registering..." : "Register"}
                        </button>
                    </div>
                </div>
            </form>
        </>
    );
}
